import { useState } from "react";
import { Twitter, Linkedin, Facebook, MessageCircle, Link2, Check } from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";

/**
 * Sosyal paylaşım butonları.
 * variant="sticky" — desktop'ta yan kenarda dikey kolon
 * variant="row"    — mobile/tablet'ta yatay sıra
 */
export function ShareButtons({ url, title, variant = "row" }) {
  const { t } = useLanguage();
  const [copied, setCopied] = useState(false);

  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const items = [
    {
      key: "twitter",
      label: "X / Twitter",
      icon: Twitter,
      href: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      hoverColor: "hover:bg-slate-900 hover:text-white hover:border-slate-900",
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      icon: Linkedin,
      href: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
      hoverColor: "hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2]",
    },
    {
      key: "facebook",
      label: "Facebook",
      icon: Facebook,
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      hoverColor: "hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2]",
    },
    {
      key: "whatsapp",
      label: "WhatsApp",
      icon: MessageCircle,
      href: `https://api.whatsapp.com/send?text=${encodedTitle}%20${encodedUrl}`,
      hoverColor: "hover:bg-[#25D366] hover:text-white hover:border-[#25D366]",
    },
  ];

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // ignore
    }
  };

  const wrapperClass =
    variant === "sticky"
      ? "flex flex-col gap-2"
      : "flex flex-wrap items-center gap-2";

  const buttonBase =
    "inline-flex items-center justify-center w-10 h-10 rounded-full border border-slate-200 text-slate-600 transition-all hover:-translate-y-0.5";

  return (
    <div className={wrapperClass}>
      {variant !== "sticky" && (
        <span className="text-xs font-medium text-slate-500 mr-2">{t("blog.share")}</span>
      )}
      {items.map((item) => (
        <a
          key={item.key}
          href={item.href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={item.label}
          title={item.label}
          className={`${buttonBase} ${item.hoverColor}`}
        >
          <item.icon className="w-4 h-4" />
        </a>
      ))}
      <button
        type="button"
        onClick={handleCopy}
        aria-label={t("blog.copyLink")}
        title={t("blog.copyLink")}
        className={`${buttonBase} ${
          copied
            ? "bg-emerald-50 text-emerald-600 border-emerald-200"
            : "hover:bg-slate-900 hover:text-white hover:border-slate-900"
        }`}
      >
        {copied ? <Check className="w-4 h-4" /> : <Link2 className="w-4 h-4" />}
      </button>
      {copied && variant !== "sticky" && (
        <span className="text-xs text-emerald-600 font-medium">{t("blog.copied")}</span>
      )}
    </div>
  );
}
